import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * FluidOverlayLayer — Iter 156.
 *
 * Renders the per-section grid overlay + bottom resize handle via a React
 * portal at document.body so they are NEVER children of a React-managed
 * section. Eliminates the "Failed to execute removeChild on Node"
 * reconciliation warnings the imperative implementation produced.
 *
 * Tracks each section's screen rect via ResizeObserver + window scroll.
 * Positions overlays via inline style so they hover precisely above each
 * section's bounding box.
 */
export default function FluidOverlayLayer({ canvasRef, blocks, onStartResize }) {
  const [rects, setRects] = useState({}); // blockId → {top,left,width,height}

  const recompute = useCallback(() => {
    const root = canvasRef?.current || document;
    const sections = Array.from(root.querySelectorAll('[data-testid^="live-block-"]'));
    const next = {};
    sections.forEach(s => {
      const id = s.getAttribute('data-block-id');
      if (!id) return;
      // Ensure the section is positioned so absolute children inside the
      // canvas content still line up; cheap to set once.
      const cs = window.getComputedStyle(s);
      if (cs.position === 'static') s.style.position = 'relative';
      const r = s.getBoundingClientRect();
      // Round to whole pixels so sub-pixel jitter during text edits /
      // resize observers does not trigger a re-render every frame.
      next[id] = {
        top: Math.round(r.top),
        left: Math.round(r.left),
        width: Math.round(r.width),
        height: Math.round(r.height),
      };
    });
    // Iter 160 — only commit when the rects actually changed. Without
    // this guard, every scroll event / ResizeObserver tick produced a
    // brand-new state object → portal re-render → child <button>s blinked
    // (user reported the "+ ADD BLOCK" pill flickering / jumping).
    setRects(prev => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length) {
        let changed = false;
        for (const k of nextKeys) {
          const a = prev[k];
          const b = next[k];
          if (!a || a.top !== b.top || a.left !== b.left || a.width !== b.width || a.height !== b.height) {
            changed = true;
            break;
          }
        }
        if (!changed) return prev;
      }
      return next;
    });
  }, [canvasRef]);

  // Recompute on mount, on blocks change, on resize/scroll.
  useEffect(() => {
    recompute();
    let raf = null;
    const onAny = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };
    window.addEventListener('resize', onAny);
    window.addEventListener('scroll', onAny, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onAny);
      window.removeEventListener('scroll', onAny, true);
    };
  }, [recompute, blocks]);

  // ResizeObserver on each section for height changes (e.g. resize handle).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const root = canvasRef?.current || document;
    const sections = Array.from(root.querySelectorAll('[data-testid^="live-block-"]'));
    const ro = new ResizeObserver(recompute);
    sections.forEach(s => ro.observe(s));
    return () => ro.disconnect();
  }, [canvasRef, blocks, recompute]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div data-wb-fluid-overlay-layer="1" className="pointer-events-none" style={{ position: 'fixed', inset: 0, zIndex: 5 }}>
      {Object.entries(rects).map(([blockId, r]) => (
        <React.Fragment key={blockId}>
          {/* Grid overlay (pure visual — CSS targets [data-wb-grid-active] / body[data-wb-grid-dragging]) */}
          <div
            className="wb-grid-overlay-portal"
            data-block-id={blockId}
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: r.top,
              left: r.left,
              width: r.width,
              height: r.height,
            }}
          />
          {/* Resize handle (interactive) */}
          <button
            type="button"
            className="wb-section-resize-handle-portal pointer-events-auto"
            data-testid={`section-resize-${blockId}`}
            onMouseDown={(e) => onStartResize?.(e, blockId)}
            style={{
              position: 'absolute',
              top: r.top + r.height - 14,
              left: r.left + r.width / 2 - 40,
              width: 80,
              height: 24,
            }}
          >
            ⬍ Resize
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  );
}
